import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../sectepe-ui/index';
import { workflowApi } from '../services/api';
import { PageHeader } from '../components/ui';
import MarkdownView from '../components/MarkdownView';
import { Icon, IconName } from '../components/icons';
import { useUrlParams } from '../vertrieb/useUrlParams';
import { fehlerText } from '../vertrieb/format';
import { schritteAus } from './workflowSchritte';

export interface WorkflowFeld {
  name: string;
  label: string;
  pflicht?: boolean;
  mehrzeilig?: boolean;
  zeilen?: number;
  platzhalter?: string;
  optionen?: string[];
  standard?: string;
}

export interface WorkflowVariante {
  id: string;
  label: string;
  beschreibung: string;
  /** Name des gespeicherten Workflows (aus "Vorlagen laden"). */
  workflowName: string;
  felder: WorkflowFeld[];
}

export interface WorkflowSeitenConfig {
  titel: string;
  untertitel: string;
  icon: IconName;
  startLabel: string;
  ergebnisTitel: string;
  varianten: WorkflowVariante[];
}

type Schrittstand = { name: string; zustand: 'offen' | 'laeuft' | 'fertig'; inhalt?: string };

const startwerte = (v: WorkflowVariante) =>
  Object.fromEntries(v.felder.map((f) => [f.name, f.standard ?? '']));

/**
 * Mehrstufige KI-Erstellung (E-Mail, Blog): Variante waehlen, Angaben
 * ausfuellen, die Schritte des gespeicherten Workflows laufen nacheinander,
 * jeder Schritt bekommt die Ergebnisse der vorigen als Variable. Fehlt der
 * Workflow, laesst er sich hier direkt aus den Vorlagen anlegen.
 */
export const MehrstufigerWorkflow: React.FC<{ config: WorkflowSeitenConfig }> = ({ config }) => {
  const { push } = useToast();
  const { get, set } = useUrlParams();
  const variante = config.varianten.find((v) => v.id === get('typ')) ?? config.varianten[0];

  const [werte, setWerte] = useState<Record<string, Record<string, string>>>(
    () => Object.fromEntries(config.varianten.map((v) => [v.id, startwerte(v)])),
  );
  const [versucht, setVersucht] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [schritte, setSchritte] = useState<Schrittstand[]>([]);
  const [ergebnis, setErgebnis] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [fehltWorkflow, setFehltWorkflow] = useState(false);
  const [laedtVorlagen, setLaedtVorlagen] = useState(false);

  const eingaben = werte[variante.id];
  const fehlend = useMemo(
    () => variante.felder.filter((f) => f.pflicht && !eingaben[f.name]?.trim()),
    [variante, eingaben],
  );

  const waehlen = (id: string) => {
    set('typ', id === config.varianten[0].id ? null : id, { replace: true });
    setSchritte([]);
    setErgebnis('');
    setFehler(null);
    setFehltWorkflow(false);
    setVersucht(false);
  };

  const aendern = (name: string, wert: string) =>
    setWerte((alt) => ({ ...alt, [variante.id]: { ...alt[variante.id], [name]: wert } }));

  const starten = async () => {
    setVersucht(true);
    if (fehlend.length) {
      setFehler(`Bitte noch ausfüllen: ${fehlend.map((f) => f.label).join(', ')}.`);
      return;
    }
    setFehler(null);
    setFehltWorkflow(false);
    setErgebnis('');
    setLaeuft(true);
    try {
      const alle = (await workflowApi.getWorkflows()) ?? [];
      const workflow = alle.find((w: any) => w.name === variante.workflowName);
      if (!workflow) {
        setFehltWorkflow(true);
        setSchritte([]);
        return;
      }
      const liste = schritteAus(workflow);
      const stand: Schrittstand[] = liste.map((s) => ({ name: s.name, zustand: 'offen' }));
      setSchritte(stand);
      const variablen: Record<string, string> = { ...eingaben };
      let letzter = '';
      for (let i = 0; i < liste.length; i += 1) {
        setSchritte((alt) => alt.map((s, j) => (j === i ? { ...s, zustand: 'laeuft' } : s)));
        const antwort = await workflowApi.executeWorkflowStep(workflow.id, { variables: variablen, stepIndex: i });
        const inhalt: string = antwort?.step?.content ?? '';
        variablen[`step_${liste[i].id}_result`] = inhalt;
        letzter = inhalt;
        setSchritte((alt) => alt.map((s, j) => (j === i ? { ...s, zustand: 'fertig', inhalt } : s)));
      }
      setErgebnis(letzter);
      push({ message: `${config.ergebnisTitel} ist fertig.`, severity: 'success' });
    } catch (e) {
      setFehler(fehlerText(e, 'Der Workflow ist abgebrochen'));
    } finally {
      setLaeuft(false);
    }
  };

  const vorlagenLaden = async () => {
    setLaedtVorlagen(true);
    try {
      const b = await workflowApi.seedWorkflows();
      push({ message: b?.created ? `${b.created} Workflow-Vorlagen angelegt.` : 'Vorlagen sind vorhanden.', severity: 'success' });
      setFehltWorkflow(false);
      await starten();
    } catch (e) {
      setFehler(fehlerText(e, 'Vorlagen konnten nicht geladen werden'));
    } finally {
      setLaedtVorlagen(false);
    }
  };

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(ergebnis);
      push({ message: 'In die Zwischenablage kopiert.', severity: 'success' });
    } catch {
      push({ message: 'Kopieren nicht möglich – bitte markieren und manuell kopieren.', severity: 'warning' });
    }
  };

  const fertig = schritte.filter((s) => s.zustand === 'fertig').length;

  return (
    <div className="page-container">
      <PageHeader eyebrow="Inhalte & Werkzeuge" title={config.titel} subtitle={config.untertitel} />

      <section aria-label="Art wählen">
        <div className="varianten" role="radiogroup" aria-label="Art">
          {config.varianten.map((v) => (
            <button
              key={v.id}
              type="button"
              role="radio"
              aria-checked={v.id === variante.id}
              className={`variante ${v.id === variante.id ? 'variante--aktiv' : ''}`}
              onClick={() => waehlen(v.id)}
              disabled={laeuft}
            >
              <Icon name={config.icon} size={18} />
              <span>
                <strong>{v.label}</strong>
                <small>{v.beschreibung}</small>
              </span>
            </button>
          ))}
        </div>
      </section>

      <form
        className="card inhalt-formular"
        aria-label={`Angaben: ${variante.label}`}
        onSubmit={(e) => { e.preventDefault(); starten(); }}
      >
        <h2 className="card-title">Angaben für „{variante.label}“</h2>
        <div className="inhalt-felder">
          {variante.felder.map((f) => {
            const ungueltig = versucht && f.pflicht && !eingaben[f.name]?.trim();
            const props = {
              value: eingaben[f.name] ?? '',
              'aria-invalid': ungueltig || undefined,
              onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => aendern(f.name, e.target.value),
            };
            return (
              <label key={f.name} className={f.mehrzeilig ? 'inhalt-felder__breit' : undefined}>
                {f.label}{f.pflicht && ' *'}
                {f.optionen ? (
                  <select {...props}>
                    <option value="">Bitte wählen…</option>
                    {f.optionen.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.mehrzeilig ? (
                  <textarea rows={f.zeilen ?? 3} placeholder={f.platzhalter} {...props} />
                ) : (
                  <input type="text" placeholder={f.platzhalter} {...props} />
                )}
              </label>
            );
          })}
        </div>
        {fehler && <div className="alert alert-error">{fehler}</div>}
        {fehltWorkflow && (
          <div className="flow-banner flow-banner--blocked" role="alert">
            <Icon name="alert" size={16} />
            <span>Der Workflow „{variante.workflowName}“ ist noch nicht angelegt. Die Vorlagen bringen ihn mit.</span>
            <button type="button" className="btn btn-primary btn-sm" onClick={vorlagenLaden} disabled={laedtVorlagen}>
              {laedtVorlagen ? 'Lädt…' : 'Vorlagen laden und starten'}
            </button>
          </div>
        )}
        <div className="btn-group">
          <button type="submit" className="btn btn-primary" disabled={laeuft}>
            <Icon name="sparkles" size={14} /> {laeuft ? `Läuft… (${fertig} von ${schritte.length || '?'})` : config.startLabel}
          </button>
          <span className="text-xs text-secondary">* Pflichtfeld</span>
        </div>
      </form>

      {schritte.length > 0 && (
        <section className="card" aria-label="Fortschritt">
          <h2 className="card-title">Schritte</h2>
          <ol className="durchlauf-schritte">
            {schritte.map((s, i) => (
              <li key={`${s.name}-${i}`} className="durchlauf-schritt" data-state={s.zustand === 'fertig' ? 'done' : s.zustand === 'laeuft' ? 'ready' : 'blocked'}>
                <span className="durchlauf-schritt__nr">{s.zustand === 'fertig' ? <Icon name="check" size={14} /> : i + 1}</span>
                <span className="durchlauf-schritt__text">
                  <strong>{s.name}</strong>
                  <small>{s.zustand === 'fertig' ? 'fertig' : s.zustand === 'laeuft' ? 'läuft…' : 'wartet'}</small>
                </span>
              </li>
            ))}
          </ol>
          {schritte.some((s) => s.inhalt) && (
            <div className="zwischenschritte">
              {schritte.slice(0, ergebnis ? -1 : undefined).filter((s) => s.inhalt).map((s, i) => (
                <details key={`${s.name}-${i}`}>
                  <summary>Zwischenergebnis: {s.name}</summary>
                  <div className="result-container"><MarkdownView content={s.inhalt || ''} /></div>
                </details>
              ))}
            </div>
          )}
        </section>
      )}

      {ergebnis && (
        <section className="card" aria-label={config.ergebnisTitel}>
          <div className="inhalt-ergebnis__kopf">
            <h2 className="card-title">{config.ergebnisTitel}</h2>
            <div className="btn-group">
              <button type="button" className="btn btn-secondary btn-sm" onClick={kopieren}>
                <Icon name="copy" size={14} /> Kopieren
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={starten} disabled={laeuft}>
                <Icon name="refresh" size={14} /> Neu erzeugen
              </button>
            </div>
          </div>
          <div className="result-container"><MarkdownView content={ergebnis} /></div>
          <p className="text-xs text-secondary">
            Die Schritte lassen sich unter <Link to="/workflows">Meine Workflows</Link> anpassen.
          </p>
        </section>
      )}
    </div>
  );
};
